/**
 * HOF-307 (S4 of HOF-299) — the words on a material-input Posten (a Düngung's or
 * Pflanzenschutz's article) under the „Dünger/PSM" line: how its € was reached, or why it
 * carries none. The buy-side twin of ``leistungMark``'s ``postenNote``.
 *
 * The cascade the worker walks (``input_valuation``): the cost the ingest booked off the
 * Erntejahr's price list → the farm's OWN purchase price on the product tile → the
 * Erntejahr's MyFarm24 list price → the curated reference price → nothing. Each basis has
 * its own sentence, because „bewertet mit deinem Einkaufspreis" and „geschätzt aus einem
 * Richtpreis" are different claims about the same €, and the farmer is entitled to which.
 */
import type { InputPostenItem, InputRefusal } from './types';

const NUM = new Intl.NumberFormat('de-DE');

/** Why an input has no price — and that it therefore stands with 0,00 € in the bucket. */
export const INPUT_REFUSAL_NOTE: Record<InputRefusal, string> = {
  no_article: 'Preis fehlt: die Buchung nennt keinen Artikel, deshalb steht sie mit 0,00 € in den Kosten.',
  no_quantity: 'Preis fehlt: die Buchung nennt keine Menge, deshalb steht sie mit 0,00 € in den Kosten.',
  no_product:
    'Preis fehlt: für diesen Artikel gibt es noch keine Kachel im Preisspiegel — weder ein Einkauf noch ein ' +
    'Listenpreis noch ein Richtpreis ist hinterlegt. Die Buchung steht mit 0,00 € in den Kosten.',
  no_price_for_product:
    'Preis fehlt: für diesen Artikel liegt weder ein eigener Einkaufspreis noch ein Listenpreis des Erntejahrs ' +
    'noch ein Richtpreis vor. Die Buchung steht mit 0,00 € in den Kosten.',
  unconvertible_unit:
    'Preis fehlt: die gebuchte Einheit lässt sich nicht in die Preiseinheit umrechnen (z. B. Liter gegen €/kg — ' +
    'dafür fehlt die Dichte). Die Buchung steht mit 0,00 € in den Kosten.',
};

/** The chip word on an input that carries no price. */
export const INPUT_UNPRICED_LABEL = 'Preis fehlt';

/** The hover on a priced or refused input Posten. */
export function inputPostenNote(posten: InputPostenItem): string {
  if (!posten.valued) return posten.reason ? INPUT_REFUSAL_NOTE[posten.reason] : INPUT_UNPRICED_LABEL;
  const parts: string[] = [];
  switch (posten.basis) {
    case 'booked':
      parts.push('Gebucht: mit dem Listenpreis des Erntejahrs bei der Übernahme aus MyFarm24 berechnet');
      break;
    case 'observed':
      if (posten.carried_from) {
        parts.push(
          `Bewertet mit deinem Einkaufspreis aus ${posten.carried_from} — für ${posten.price_month ?? 'diesen Monat'} ` +
            'liegt kein eigener Einkauf vor',
        );
      } else {
        parts.push(`Bewertet mit deinem Einkaufspreis ${posten.price_month ?? ''}`.trim());
      }
      if (posten.observation_count !== undefined) {
        const sellers =
          posten.counterparty_count !== undefined ? ` von ${NUM.format(posten.counterparty_count)} Lieferanten` : '';
        parts.push(`Ø aus ${NUM.format(posten.observation_count)} Rechnungspositionen${sellers}`);
      }
      break;
    case 'list_price':
      parts.push(
        posten.price_year !== undefined
          ? `Bewertet mit dem MyFarm24-Listenpreis für das Erntejahr ${posten.price_year}`
          : 'Bewertet mit dem MyFarm24-Listenpreis',
      );
      break;
    case 'estimate':
      parts.push('Geschätzt: Richtpreis aus dem hinterlegten Preisbuch — kein eigener Einkauf und kein Listenpreis');
      if (posten.source) parts.push(`Quelle: ${posten.source}`);
      break;
    default:
      parts.push('Bewertet');
  }
  return `${parts.join(' · ')}.`;
}
